// AdminOnboardingPage — voice picker per domain + tour analytics.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import {
  Loader2, AlertCircle, Volume2, Users, PlayCircle, BarChart3,
  CheckCircle2, Building2, Radio,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOnboardingPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [voices, setVoices] = useState(null);
  const [domains, setDomains] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [audioUsers, setAudioUsers] = useState(null);
  const [quota, setQuota] = useState(null);
  const [audioUserBusy, setAudioUserBusy] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState({});   // { [domain_id]: true }
  const [previewBusy, setPreviewBusy] = useState({});   // { [voice_id]: true }
  const [previewErr, setPreviewErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [voicesR, analyticsR, audioUsersR, quotaR] = await Promise.all([
        axios.get(`${API}/onboarding/admin/voices`, { headers }),
        axios.get(`${API}/onboarding/admin/analytics?days=30`, { headers }),
        axios.get(`${API}/onboarding/admin/digest-audio-users`, { headers }),
        axios.get(`${API}/onboarding/admin/quota`, { headers }).catch(() => ({ data: null })),
      ]);
      setVoices(voicesR.data.voices);
      setDomains(voicesR.data.domains);
      setAnalytics(analyticsR.data);
      setAudioUsers(audioUsersR.data.users);
      setQuota(quotaR.data);
    } catch (e) { setErr(e?.response?.data?.detail || 'Failed to load.'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const setVoice = async (domain_id, voice_id) => {
    setBusy((s) => ({ ...s, [domain_id]: true }));
    try {
      await axios.patch(`${API}/onboarding/admin/voices/${domain_id}`, { voice_id }, { headers });
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || 'Save failed.'); }
    finally { setBusy((s) => { const n = { ...s }; delete n[domain_id]; return n; }); }
  };

  const toggleAudioUser = async (user_id, enabled) => {
    setAudioUserBusy((s) => ({ ...s, [user_id]: true }));
    try {
      await axios.patch(`${API}/onboarding/admin/digest-audio-users/${user_id}`,
        { enabled }, { headers });
      setAudioUsers((rows) => rows.map((r) => (r.id === user_id ? { ...r, digest_audio_enabled: enabled } : r)));
    } catch (e) { setErr(e?.response?.data?.detail || 'Toggle failed.'); }
    finally { setAudioUserBusy((s) => { const n = { ...s }; delete n[user_id]; return n; }); }
  };

  const previewVoice = async (voice_id) => {
    setPreviewBusy((s) => ({ ...s, [voice_id]: true })); setPreviewErr('');
    try {
      // Uses the new /preview/{voice_id} endpoint — no domain assignment needed
      const r = await axios.get(`${API}/onboarding/preview/${voice_id}`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const audio = new Audio(url);
      audio.play().catch(() => setPreviewErr('Autoplay blocked — click again.'));
    } catch (e) { setPreviewErr(e?.response?.data?.detail || 'Preview failed.'); }
    finally { setPreviewBusy((s) => { const n = { ...s }; delete n[voice_id]; return n; }); }
  };

  if (loading) {
    return <AdminLayout><div className="p-6 flex items-center justify-center min-h-[50vh]"><Loader2 className="w-6 h-6 text-white/40 animate-spin" /></div></AdminLayout>;
  }
  if (err) {
    return <AdminLayout><div className="p-6 max-w-3xl mx-auto"><div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-200 flex items-start gap-2"><AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{err}</div></div></AdminLayout>;
  }

  const maxCount = Math.max(1, ...(analytics?.per_step || []).map(
    (s) => (s.closed || 0) + (s.completed || 0)
  ));

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="admin-onboarding-page">
        <header className="mb-5">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Volume2 className="w-6 h-6 text-teal-400" />
            Onboarding voice tour
          </h1>
          <p className="text-sm text-white/60 mt-1">Pick the Australian voice each tenant hears, and see where consultants drop off during the tour.</p>
        </header>

        {/* Voice picker */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-white font-semibold text-sm">
            <Users className="w-4 h-4 text-teal-400" />Voice per tenant
            {quota && quota.limit > 0 && (
              <div className="ml-auto flex items-center gap-2" data-testid="elevenlabs-quota">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">
                  {quota.tier || 'plan'} · this cycle
                </div>
                <div className="text-xs text-white/70 tabular-nums">
                  <span className={`font-bold ${
                    quota.used_pct >= 0.9 ? 'text-rose-300'
                    : quota.used_pct >= 0.7 ? 'text-amber-300'
                    : 'text-emerald-300'}`}>
                    {(quota.used || 0).toLocaleString()}
                  </span>
                  <span className="text-white/40"> / {(quota.limit || 0).toLocaleString()} chars</span>
                </div>
                <div className="w-16 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className={`h-full transition-all ${
                    quota.used_pct >= 0.9 ? 'bg-rose-500'
                    : quota.used_pct >= 0.7 ? 'bg-amber-500'
                    : 'bg-emerald-500'}`}
                    style={{ width: `${Math.min(100, (quota.used_pct || 0) * 100)}%` }} />
                </div>
              </div>
            )}
          </div>

          {/* Available voices strip */}
          <div className="px-4 py-3 border-b border-white/5 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="voice-catalogue">
            {voices.map((v) => (
              <div key={v.voice_id} className="rounded border border-white/10 bg-white/5 p-2.5" data-testid={`voice-card-${v.label}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-white font-semibold text-sm">{v.label}</div>
                  <button
                    type="button"
                    onClick={() => previewVoice(v.voice_id)}
                    disabled={previewBusy[v.voice_id]}
                    className="text-teal-300 hover:text-teal-200 disabled:opacity-40"
                    data-testid={`voice-preview-${v.label}`}
                    title="Preview welcome"
                  >
                    {previewBusy[v.voice_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-5 h-5" />}
                  </button>
                </div>
                <div className="text-[11px] text-white/60">{v.description}</div>
              </div>
            ))}
          </div>
          {previewErr && <div className="mx-4 my-2 text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1.5">{previewErr}</div>}

          {/* Per-domain picker rows */}
          <div className="divide-y divide-white/5">
            {domains.map((d) => (
              <div key={d.domain_id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`domain-voice-row-${d.domain_id}`}>
                <Building2 className="w-4 h-4 text-teal-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{d.name}</div>
                  <div className="text-[11px] text-white/50 font-mono truncate">{d.domain_id}</div>
                </div>
                <select
                  value={d.voice_id}
                  onChange={(e) => setVoice(d.domain_id, e.target.value === '__default__' ? '' : e.target.value)}
                  disabled={busy[d.domain_id]}
                  className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs"
                  data-testid={`domain-voice-select-${d.domain_id}`}
                >
                  {voices.map((v) => (
                    <option key={v.voice_id} value={v.voice_id} className="bg-slate-800">
                      {v.label}{d.is_default && v.voice_id === d.voice_id ? ' (shared default)' : ''}
                    </option>
                  ))}
                  <option value="__default__" className="bg-slate-800">— Revert to shared default —</option>
                </select>
                {busy[d.domain_id] && <Loader2 className="w-4 h-4 animate-spin text-white/40" />}
                {d.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-300 border border-slate-500/40">shared</span>}
                {!d.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-500/20 text-teal-300 border border-teal-500/40">override</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Digest audio opt-in */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-white font-semibold text-sm">
            <Radio className="w-4 h-4 text-teal-400" />
            Digest audio opt-in
            <span className="text-xs text-white/40 font-normal">— only these users get a ▶ Play digest button in the Monday email (keeps TTS cost low)</span>
          </div>
          {audioUsers && audioUsers.length === 0 && (
            <div className="text-white/40 text-xs italic p-4">No managers or admins found.</div>
          )}
          {audioUsers && audioUsers.length > 0 && (
            <div className="divide-y divide-white/5" data-testid="digest-audio-users">
              {audioUsers.map((u) => (
                <div key={u.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`digest-audio-row-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold text-sm truncate">{u.name || u.email || u.id}</div>
                    <div className="text-[11px] text-white/50 font-mono truncate">{u.email} · {u.role}</div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={u.digest_audio_enabled}
                      onChange={(e) => toggleAudioUser(u.id, e.target.checked)}
                      disabled={audioUserBusy[u.id]}
                      className="rounded"
                      data-testid={`digest-audio-toggle-${u.id}`}
                    />
                    <span className={`text-xs ${u.digest_audio_enabled ? 'text-emerald-300 font-semibold' : 'text-white/50'}`}>
                      {u.digest_audio_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                  {audioUserBusy[u.id] && <Loader2 className="w-3 h-3 animate-spin text-white/40" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Analytics */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-white font-semibold text-sm">
            <BarChart3 className="w-4 h-4 text-teal-400" />
            Tour analytics — last {analytics.window_days} days
            <span className="text-xs text-white/40 font-normal">
              {analytics.unique_users} consultant{analytics.unique_users === 1 ? '' : 's'} · {analytics.total_events} events
            </span>
          </div>
          <div className="p-4" data-testid="tour-analytics">
            {analytics.total_events === 0 ? (
              <div className="text-white/40 text-xs italic">No tour events logged yet. Data will appear here once consultants open the tour.</div>
            ) : (
              <div className="space-y-2">
                {analytics.per_step.map((s, i) => {
                  const total = (s.closed || 0) + (s.completed || 0);
                  const closedPct = maxCount > 0 ? Math.round((s.closed / maxCount) * 100) : 0;
                  const completedPct = maxCount > 0 ? Math.round((s.completed / maxCount) * 100) : 0;
                  return (
                    <div key={s.step_id} className="grid grid-cols-12 gap-2 items-center text-xs" data-testid={`analytics-row-${s.step_id}`}>
                      <div className="col-span-4 text-white/80 truncate">
                        <span className="text-white/40 font-mono">{i + 1}.</span> {s.title}
                      </div>
                      <div className="col-span-6 h-6 bg-white/5 rounded overflow-hidden flex">
                        <div className="h-full bg-emerald-500/70" style={{ width: `${completedPct}%` }} title={`completed: ${s.completed}`} />
                        <div className="h-full bg-rose-500/70" style={{ width: `${closedPct}%` }} title={`closed: ${s.closed}`} />
                      </div>
                      <div className="col-span-2 text-right tabular-nums text-white/70">
                        <span className="text-emerald-300">{s.completed}</span> / <span className="text-rose-300">{s.closed}</span>
                      </div>
                    </div>
                  );
                })}
                <div className="mt-3 flex items-center gap-4 text-[10px] text-white/50">
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-emerald-500 rounded-sm" />completed step</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-rose-500 rounded-sm" />closed on this step</span>
                  <span className="ml-auto italic">A high &quot;closed&quot; count on any single step means the copy for that step needs work.</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminOnboardingPage;
